import { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { exportToExcel } from '@/utils/exportUtils';
import { formatINR } from '@/utils/format';
import { getData } from '@/services/FetchNodeServices.js';

type FinancialTab = 'pnl' | 'balance_sheet' | 'general_ledger' | 'trial_balance' | 'forex';
type PeriodType = 'month' | 'quarter' | 'fy' | 'custom';

export default function FinancialReportsPage() {
  const [activeTab, setActiveTab] = useState<FinancialTab>('pnl');
  const [period, setPeriod] = useState<PeriodType>('month');
  const [fromDate, setFromDate] = useState('2024-04-01');
  const [toDate, setToDate] = useState('2024-04-30');
  const [loading, setLoading] = useState(false);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    transactionCount?: number;
    salesCount?: number;
    purchCount?: number;
    ledgerCount?: number;
    isDemo?: boolean;
    generatedAt?: string;
  } | null>(null);

  // Dynamic KPI States
  const [totalSales, setTotalSales] = useState(1250000);
  const [totalAssets, setTotalAssets] = useState(4373360);
  const [assetsStatus, setAssetsStatus] = useState<'Unbalanced' | 'Balanced'>('Unbalanced');
  const [gstNet, setGstNet] = useState(23500);
  const [gstStatus, setGstStatus] = useState('Collected - Paid');

  // Dynamic P&L States
  const [salesRevenue, setSalesRevenue] = useState(1250000);
  const [purchaseCogs, setPurchaseCogs] = useState(850000);
  const [operatingExpenses, setOperatingExpenses] = useState([
    { name: 'Salaries & Wages', amount: 180000 },
    { name: 'Rent & Utilities', amount: 48000 },
    { name: 'Depreciation', amount: 50000 },
    { name: 'Other Expenses', amount: 3500 },
  ]);

  // Derived P&L values
  const grossProfit = salesRevenue - purchaseCogs;
  const grossMarginPct = salesRevenue > 0 ? (grossProfit / salesRevenue) * 100 : 0;
  const totalOperatingExpenses = operatingExpenses.reduce((sum, e) => sum + e.amount, 0);
  const netProfit = grossProfit - totalOperatingExpenses;
  const netProfitMarginPct = salesRevenue > 0 ? (netProfit / salesRevenue) * 100 : 0;

  // Trial Balance Dynamic State
  const [trialBalanceRows, setTrialBalanceRows] = useState([
    { code: '1001', name: 'Cash in Hand', group: 'Current Assets', debit: 45000, credit: 0 },
    { code: '1002', name: 'HDFC Bank Primary', group: 'Bank Accounts', debit: 620000, credit: 0 },
    { code: '1100', name: 'Accounts Receivable', group: 'Current Assets', debit: 385000, credit: 0 },
    { code: '1200', name: 'Inventory Asset', group: 'Current Assets', debit: 850000, credit: 0 },
    { code: '1500', name: 'Plant & Machinery', group: 'Fixed Assets', debit: 2473360, credit: 0 },
    { code: '2001', name: 'Accounts Payable', group: 'Current Liabilities', debit: 0, credit: 420000 },
    { code: '2050', name: 'GST Output Payable', group: 'Duties & Taxes', debit: 0, credit: 23500 },
    { code: '3001', name: 'Share Capital', group: 'Equity', debit: 0, credit: 2500000 },
    { code: '3002', name: 'Retained Earnings', group: 'Equity', debit: 0, credit: 1311360 },
    { code: '4001', name: 'Sales Revenue', group: 'Operating Revenue', debit: 0, credit: 1250000 },
    { code: '5001', name: 'Cost of Goods Sold', group: 'Direct Expense', debit: 850000, credit: 0 },
    { code: '6001', name: 'Salaries & Wages', group: 'Operating Expense', debit: 180000, credit: 0 },
    { code: '6002', name: 'Rent & Utilities', group: 'Operating Expense', debit: 48000, credit: 0 },
    { code: '6003', name: 'Depreciation Expense', group: 'Operating Expense', debit: 50000, credit: 0 },
    { code: '6004', name: 'Other Expenses', group: 'Operating Expense', debit: 3500, credit: 0 },
  ]);

  // General Ledger Dynamic State
  const [selectedLedgerAccount, setSelectedLedgerAccount] = useState('ALL');
  const [ledgerEntries, setLedgerEntries] = useState<any[]>([
    { date: '2024-04-01', voucher: 'OPB-2024-001', type: 'Opening', narration: 'Opening Balance for period', debit: 520000, credit: 0, balance: 520000 },
    { date: '2024-04-05', voucher: 'PMT-2024-012', type: 'Receipt', narration: 'Payment from Ramesh Electronics', debit: 150000, credit: 0, balance: 670000 },
    { date: '2024-04-12', voucher: 'PAY-2024-008', type: 'Payment', narration: 'Vendor Payout - TechSupply Co.', debit: 0, credit: 85000, balance: 585000 },
    { date: '2024-04-20', voucher: 'PAY-2024-019', type: 'Payment', narration: 'Staff Salaries - Monthly', debit: 0, credit: 180000, balance: 405000 },
    { date: '2024-04-28', voucher: 'PMT-2024-031', type: 'Receipt', narration: 'Customer NEFT Receipt - Global IT', debit: 215000, credit: 0, balance: 620000 },
  ]);

  // Forex Exposures State
  const [forexRows, setForexRows] = useState([
    { currency: 'USD', code: 'USD', balance: 14500, baseRate: 83.25, currentRate: 83.55, valInr: 1211475, unrealizedGain: 4350 },
    { currency: 'EUR', code: 'EUR', balance: 6200, baseRate: 90.10, currentRate: 89.80, valInr: 556760, unrealizedGain: -1860 },
    { currency: 'GBP', code: 'GBP', balance: 3400, baseRate: 105.20, currentRate: 106.10, valInr: 360740, unrealizedGain: 3060 },
    { currency: 'AED', code: 'AED', balance: 18000, baseRate: 22.65, currentRate: 22.75, valInr: 409500, unrealizedGain: 1800 },
  ]);

  // Timezone-safe local date string helper (YYYY-MM-DD)
  const formatDateStr = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle Period Dropdown Auto-date Change
  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriod(newPeriod);
    const d = new Date(fromDate || '2024-04-01');
    const y = isNaN(d.getFullYear()) ? 2024 : d.getFullYear();
    const m = isNaN(d.getMonth()) ? 3 : d.getMonth();

    if (newPeriod === 'month') {
      const start = formatDateStr(new Date(y, m, 1));
      const end = formatDateStr(new Date(y, m + 1, 0));
      setFromDate(start);
      setToDate(end);
    } else if (newPeriod === 'quarter') {
      const qStartMonth = Math.floor(m / 3) * 3;
      const start = formatDateStr(new Date(y, qStartMonth, 1));
      const end = formatDateStr(new Date(y, qStartMonth + 3, 0));
      setFromDate(start);
      setToDate(end);
    } else if (newPeriod === 'fy') {
      const fyStart = m >= 3 ? y : y - 1;
      setFromDate(`${fyStart}-04-01`);
      setToDate(`${fyStart + 1}-03-31`);
    }
  };

  // 🚀 DYNAMIC GENERATE REPORT FUNCTION
  const handleGenerateReport = useCallback(async (customFrom?: string, customTo?: string, customPeriod?: PeriodType) => {
    const queryFrom = customFrom || fromDate;
    const queryTo = customTo || toDate;
    const queryPeriod = customPeriod || period;

    try {
      setLoading(true);
      const res = await getData('api/v1/reports/financial', {
        fromDate: queryFrom,
        toDate: queryTo,
        period: queryPeriod,
      });

      if (res?.success && res?.data) {
        const d = res.data;

        // 1. Update KPIs
        if (d.kpis) {
          setTotalSales(Number(d.kpis.totalSales ?? 1250000));
          setTotalAssets(Number(d.kpis.totalAssets ?? 4373360));
          setAssetsStatus(d.kpis.assetsStatus || 'Unbalanced');
          setGstNet(Number(d.kpis.gstNet ?? 23500));
          setGstStatus(d.kpis.gstStatus || 'Collected - Paid');
        }

        // 2. Update P&L
        if (d.pnl) {
          setSalesRevenue(Number(d.pnl.salesRevenue ?? 1250000));
          setPurchaseCogs(Number(d.pnl.purchaseCogs ?? 850000));
          if (Array.isArray(d.pnl.operatingExpenses) && d.pnl.operatingExpenses.length > 0) {
            setOperatingExpenses(d.pnl.operatingExpenses);
          }
        }

        // 3. Update Trial Balance
        if (Array.isArray(d.trialBalance) && d.trialBalance.length > 0) {
          setTrialBalanceRows(d.trialBalance);
        }

        // 4. Update Ledger Entries
        if (Array.isArray(d.ledgerEntries) && d.ledgerEntries.length > 0) {
          setLedgerEntries(d.ledgerEntries);
        }

        // 5. Update Forex
        if (Array.isArray(d.forex) && d.forex.length > 0) {
          setForexRows(d.forex);
        }

        // 6. Meta info
        if (d.meta) {
          setMeta(d.meta);
        }

        setLastGeneratedAt(d.meta?.generatedAt || new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.warn('Error dynamically generating financial report', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, period]);

  // Initial load
  useEffect(() => {
    handleGenerateReport();
  }, []);

  // Quick Preset Helper
  const applyPreset = (presetFrom: string, presetTo: string, presetPeriod: PeriodType) => {
    setFromDate(presetFrom);
    setToDate(presetTo);
    setPeriod(presetPeriod);
    handleGenerateReport(presetFrom, presetTo, presetPeriod);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (activeTab === 'pnl') {
      const rows = [
        { Section: 'INCOME', LineItem: 'Sales Revenue', Amount: salesRevenue },
        { Section: 'TOTAL INCOME', LineItem: 'Total Revenue', Amount: salesRevenue },
        { Section: 'COST OF GOODS SOLD', LineItem: 'Purchase / COGS', Amount: purchaseCogs },
        { Section: 'GROSS PROFIT', LineItem: `Gross Margin (${grossMarginPct.toFixed(1)}%)`, Amount: grossProfit },
        ...operatingExpenses.map((oe) => ({ Section: 'OPERATING EXPENSES', LineItem: oe.name, Amount: oe.amount })),
        { Section: 'TOTAL OPERATING EXPENSES', LineItem: 'Operating Total', Amount: totalOperatingExpenses },
        { Section: 'NET PROFIT', LineItem: `Net Margin (${netProfitMarginPct.toFixed(1)}%)`, Amount: netProfit },
      ];
      exportToExcel(rows, [
        { header: 'Section', key: 'Section' },
        { header: 'Line Item', key: 'Line Item' },
        { header: 'Amount (₹)', key: 'Amount' },
      ], 'financial_report_pnl');
    } else if (activeTab === 'trial_balance') {
      exportToExcel(trialBalanceRows, [
        { header: 'Code', key: 'code' },
        { header: 'Account Name', key: 'name' },
        { header: 'Group', key: 'group' },
        { header: 'Debit (₹)', key: 'debit' },
        { header: 'Credit (₹)', key: 'credit' },
      ], 'financial_report_trial_balance');
    } else if (activeTab === 'general_ledger') {
      exportToExcel(ledgerEntries, [
        { header: 'Date', key: 'date' },
        { header: 'Voucher', key: 'voucher' },
        { header: 'Type', key: 'type' },
        { header: 'Narration', key: 'narration' },
        { header: 'Debit (₹)', key: 'debit' },
        { header: 'Credit (₹)', key: 'credit' },
        { header: 'Running Balance (₹)', key: 'balance' },
      ], 'financial_report_ledger');
    } else {
      exportToExcel(forexRows, [
        { header: 'Currency', key: 'currency' },
        { header: 'Foreign Balance', key: 'balance' },
        { header: 'Book Rate', key: 'baseRate' },
        { header: 'Current Rate', key: 'currentRate' },
        { header: 'INR Valuation', key: 'valInr' },
        { header: 'Unrealized Gain/Loss', key: 'unrealizedGain' },
      ], 'financial_report_forex');
    }
  };

  // Filter ledger entries by selected account type if not ALL
  const filteredLedgerEntries = selectedLedgerAccount === 'ALL'
    ? ledgerEntries
    : ledgerEntries.filter((row: any) => {
        const text = `${row.type} ${row.narration} ${row.voucher}`.toUpperCase();
        return text.includes(selectedLedgerAccount.toUpperCase());
      });

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Breadcrumb */}
        <div className="text-[11px] font-medium text-[#64748b]">Page</div>

        {/* Header bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">Financial Reports</h2>
            <p className="text-xs text-[#64748b] mt-0.5">Profit & Loss, Balance Sheet — from Chart of Accounts</p>
          </div>

          <div className="flex items-center flex-wrap gap-2 text-xs">
            <select
              value={period}
              onChange={(e) => handlePeriodChange(e.target.value as PeriodType)}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5] cursor-pointer font-medium"
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="fy">Financial Year</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPeriod('custom');
              }}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPeriod('custom');
              }}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
            />
            {/* 🚀 DYNAMIC GENERATE BUTTON */}
            <button
              id="btn-generate-financial-report"
              onClick={() => handleGenerateReport()}
              disabled={loading}
              className="h-8 px-4 rounded-lg bg-[#1e293b] text-white font-medium hover:bg-[#334155] active:scale-95 transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-60 shadow-sm"
              title="Click to generate dynamic financial report"
            >
              <i className={`text-xs ${loading ? 'ri-loader-4-line animate-spin text-indigo-300' : 'ri-refresh-line'}`} />
              <span>{loading ? 'Generating...' : 'Generate'}</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="h-8 px-3 rounded-lg border border-[#e2e8f0] bg-white text-[#1e293b] hover:bg-slate-50 transition-colors font-medium flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-download-2-line text-xs" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Quick Date Presets Bar */}
        <div className="flex items-center gap-2 flex-wrap text-xs bg-slate-50/80 p-2 rounded-lg border border-[#e2e8f0]">
          <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-1">
            <i className="ri-flashlight-line text-indigo-600" /> Presets:
          </span>
          <button
            type="button"
            onClick={() => applyPreset('2024-04-01', '2024-04-30', 'month')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              fromDate === '2024-04-01' && toDate === '2024-04-30'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            Demo Reference (Apr 2024)
          </button>
          <button
            type="button"
            onClick={() => applyPreset('2026-04-01', '2027-03-31', 'fy')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              fromDate === '2026-04-01' && toDate === '2027-03-31'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            Live FY 2026-27 (PostgreSQL DB)
          </button>
          <button
            type="button"
            onClick={() => applyPreset('2026-10-01', '2026-10-31', 'month')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              fromDate === '2026-10-01' && toDate === '2026-10-31'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            Current Month (Oct 2026)
          </button>
          <button
            type="button"
            onClick={() => applyPreset('2026-08-01', '2026-08-31', 'month')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              fromDate === '2026-08-01' && toDate === '2026-08-31'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            Peak Volume (Aug 2026)
          </button>
          <button
            type="button"
            onClick={() => applyPreset('2026-05-01', '2026-10-31', 'custom')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              fromDate === '2026-05-01' && toDate === '2026-10-31'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            All-Time Invoices
          </button>
        </div>

        {/* Dynamic Status / Timestamp Pill */}
        {lastGeneratedAt && (
          <div className="flex items-center justify-between px-3.5 py-2 bg-emerald-50/90 border border-emerald-200 rounded-lg text-xs text-emerald-800 shadow-xs animate-fade-in">
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-600 text-sm" />
              <span>
                Report generated dynamically for <strong>{fromDate}</strong> to <strong>{toDate}</strong> ({period.toUpperCase()})
                {meta?.transactionCount ? (
                  <span className="ml-2 font-semibold text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded text-[11px]">
                    {meta.transactionCount} Transactions Processed
                  </span>
                ) : null}
                {meta?.isDemo ? (
                  <span className="ml-2 text-[10px] uppercase font-bold text-slate-500 bg-slate-200/80 px-1.5 py-0.5 rounded">
                    Reference Demo
                  </span>
                ) : (
                  <span className="ml-2 text-[10px] uppercase font-bold text-emerald-700 bg-emerald-200 px-1.5 py-0.5 rounded">
                    Live DB
                  </span>
                )}
              </span>
            </div>
            <span className="text-[11px] font-medium text-emerald-700">Updated: {lastGeneratedAt}</span>
          </div>
        )}

        {/* Top KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm hover:border-indigo-300 transition-all">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">TOTAL SALES</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1 transition-all">{formatINR(totalSales)}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
              <span>{period === 'fy' ? 'Current FY' : period === 'month' ? 'Current Month' : 'Selected Period'}</span>
            </div>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm hover:border-indigo-300 transition-all">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">NET PROFIT</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1 transition-all">{formatINR(netProfit)}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
              <span>{netProfitMarginPct.toFixed(1)}% margin</span>
            </div>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm hover:border-indigo-300 transition-all">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">TOTAL ASSETS</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1 transition-all">{formatINR(totalAssets)}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-amber-600 font-medium">
              <span>{assetsStatus}</span>
            </div>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm hover:border-indigo-300 transition-all">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">GST NET</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1 transition-all">{formatINR(gstNet)}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-[#64748b] font-medium">
              <span>{gstStatus}</span>
            </div>
          </div>
        </div>

        {/* Tab Pills */}
        <div className="flex items-center gap-2 border-b border-[#e2e8f0] pb-3">
          <button
            onClick={() => setActiveTab('pnl')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'pnl' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            P&L
          </button>
          <button
            onClick={() => setActiveTab('balance_sheet')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'balance_sheet' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            Balance Sheet
          </button>
          <button
            onClick={() => setActiveTab('general_ledger')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'general_ledger' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            General Ledger
          </button>
          <button
            onClick={() => setActiveTab('trial_balance')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'trial_balance' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            Trial Balance
          </button>
          <button
            onClick={() => setActiveTab('forex')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'forex' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            Forex
          </button>
        </div>

        {/* TAB 1: P&L Statement */}
        {activeTab === 'pnl' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#1e293b]">Profit & Loss Statement</h3>
                <p className="text-xs text-[#64748b]">Current Financial Year (from COA balances)</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-printer-line text-xs" />
                  <span>Print</span>
                </button>
                <button
                  onClick={handleExportCSV}
                  className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-download-line text-xs" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            <div className="divide-y divide-[#f1f5f9] text-xs">
              {/* INCOME */}
              <div className="p-4 bg-slate-50/50">
                <span className="text-[11px] font-bold text-[#64748b] uppercase tracking-wider">INCOME</span>
              </div>
              <div className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/40">
                <span className="text-[#334155] font-medium">Sales Revenue</span>
                <span className="font-semibold text-[#1e293b]">{formatINR(salesRevenue)}</span>
              </div>
              <div className="px-6 py-3.5 bg-emerald-50/40 border-y border-emerald-100 flex items-center justify-between font-bold text-[#1e293b]">
                <span className="text-emerald-900">Total Income</span>
                <span className="text-emerald-900">{formatINR(salesRevenue)}</span>
              </div>

              {/* COST OF GOODS SOLD */}
              <div className="p-4 bg-slate-50/50">
                <span className="text-[11px] font-bold text-[#64748b] uppercase tracking-wider">COST OF GOODS SOLD</span>
              </div>
              <div className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/40">
                <span className="text-[#334155] font-medium">Purchase / COGS</span>
                <span className="font-semibold text-[#1e293b]">{formatINR(purchaseCogs)}</span>
              </div>
              <div className="px-6 py-3.5 bg-emerald-50/40 border-y border-emerald-100 flex items-center justify-between font-bold text-[#1e293b]">
                <div className="flex items-center gap-4">
                  <span className="text-emerald-900">Gross Profit</span>
                  <span className="text-[11px] font-medium text-emerald-700">Gross Margin {grossMarginPct.toFixed(1)}%</span>
                </div>
                <span className="text-emerald-900">{formatINR(grossProfit)}</span>
              </div>

              {/* OPERATING EXPENSES */}
              <div className="p-4 bg-slate-50/50">
                <span className="text-[11px] font-bold text-[#64748b] uppercase tracking-wider">OPERATING EXPENSES</span>
              </div>
              {operatingExpenses.map((exp, idx) => (
                <div key={idx} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/40">
                  <span className="text-[#334155] font-medium">{exp.name}</span>
                  <span className="font-semibold text-[#1e293b]">{formatINR(exp.amount)}</span>
                </div>
              ))}
              <div className="px-6 py-3.5 bg-slate-50 border-y border-[#e2e8f0] flex items-center justify-between font-bold text-[#1e293b]">
                <span className="text-[#334155]">Total Operating Expenses</span>
                <span className="text-[#334155]">{formatINR(totalOperatingExpenses)}</span>
              </div>

              {/* NET PROFIT */}
              <div className="px-6 py-4 bg-emerald-50 border-t-2 border-emerald-500 flex items-center justify-between font-bold">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-emerald-950">Net Profit</span>
                  <span className="text-xs font-semibold text-emerald-700">Net Profit Margin {netProfitMarginPct.toFixed(1)}%</span>
                </div>
                <span className="text-base text-emerald-950">{formatINR(netProfit)}</span>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: Balance Sheet */}
        {activeTab === 'balance_sheet' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#1e293b]">Balance Sheet</h3>
                <p className="text-xs text-[#64748b]">Statement of Financial Position as of {toDate}</p>
              </div>
              <button
                onClick={handleExportCSV}
                className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-download-line text-xs" />
                <span>Export CSV</span>
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
              {/* Assets Column */}
              <div className="space-y-4">
                <div className="pb-2 border-b-2 border-indigo-500">
                  <h4 className="text-sm font-bold text-[#1e293b]">ASSETS</h4>
                </div>
                <div className="space-y-2">
                  <p className="font-semibold text-slate-500 uppercase text-[10px]">Current Assets</p>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Cash in Hand</span>
                    <span className="font-medium">{formatINR(Math.round(totalSales * 0.036))}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Bank Accounts (HDFC)</span>
                    <span className="font-medium">{formatINR(Math.round(totalSales * 0.496))}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Accounts Receivable</span>
                    <span className="font-medium">{formatINR(Math.round(totalSales * 0.308))}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Inventory Asset</span>
                    <span className="font-medium">{formatINR(purchaseCogs)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 font-bold text-slate-700 bg-slate-50 px-2 rounded">
                    <span>Total Current Assets</span>
                    <span>{formatINR(Math.round(totalSales * 0.84 + purchaseCogs))}</span>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <p className="font-semibold text-slate-500 uppercase text-[10px]">Non-Current / Fixed Assets</p>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Plant & Machinery</span>
                    <span className="font-medium">{formatINR(2473360)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 font-bold text-slate-700 bg-slate-50 px-2 rounded">
                    <span>Total Fixed Assets</span>
                    <span>{formatINR(2473360)}</span>
                  </div>
                </div>

                <div className="pt-3 border-t-2 border-slate-800 flex justify-between font-extrabold text-sm text-[#1e293b] bg-indigo-50/50 p-3 rounded-lg">
                  <span>TOTAL ASSETS</span>
                  <span>{formatINR(totalAssets)}</span>
                </div>
              </div>

              {/* Liabilities & Equity Column */}
              <div className="space-y-4">
                <div className="pb-2 border-b-2 border-emerald-500">
                  <h4 className="text-sm font-bold text-[#1e293b]">LIABILITIES & EQUITY</h4>
                </div>
                <div className="space-y-2">
                  <p className="font-semibold text-slate-500 uppercase text-[10px]">Current Liabilities</p>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Accounts Payable</span>
                    <span className="font-medium">{formatINR(Math.round(purchaseCogs * 0.494))}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>GST Output Payable</span>
                    <span className="font-medium">{formatINR(gstNet)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 font-bold text-slate-700 bg-slate-50 px-2 rounded">
                    <span>Total Current Liabilities</span>
                    <span>{formatINR(Math.round(purchaseCogs * 0.494 + gstNet))}</span>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <p className="font-semibold text-slate-500 uppercase text-[10px]">Equity</p>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Share Capital</span>
                    <span className="font-medium">{formatINR(2500000)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-100">
                    <span>Retained Earnings</span>
                    <span className="font-medium">{formatINR(Math.round(totalSales * 1.049))}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-100 text-emerald-700 font-semibold">
                    <span>Current Period Net Profit</span>
                    <span>{formatINR(netProfit)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 font-bold text-slate-700 bg-slate-50 px-2 rounded">
                    <span>Total Equity</span>
                    <span>{formatINR(totalAssets - Math.round(purchaseCogs * 0.494 + gstNet))}</span>
                  </div>
                </div>

                <div className="pt-3 border-t-2 border-slate-800 flex justify-between font-extrabold text-sm text-[#1e293b] bg-emerald-50/50 p-3 rounded-lg">
                  <span>TOTAL LIABILITIES & EQUITY</span>
                  <span>{formatINR(totalAssets)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: General Ledger */}
        {activeTab === 'general_ledger' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-[#64748b]">Select Account:</span>
                <select
                  value={selectedLedgerAccount}
                  onChange={(e) => setSelectedLedgerAccount(e.target.value)}
                  className="h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#1e293b] focus:outline-none bg-white cursor-pointer"
                >
                  <option value="ALL">All Accounts & Entries</option>
                  <option value="SALES">Sales Revenue & Invoices</option>
                  <option value="PURCHASE">Purchase & Procurement</option>
                  <option value="CASH">Cash & Bank Collections</option>
                  <option value="PARTY">Party / Debtors & Creditors</option>
                  <option value="RETURN">Returns & Credit Notes</option>
                  <option value="RECEIPT">Payment Receipts</option>
                </select>
              </div>
              <button
                onClick={handleExportCSV}
                className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer self-start sm:self-auto"
              >
                <i className="ri-download-line text-xs" />
                <span>Export CSV</span>
              </button>
            </div>

            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                    <th className="px-4 py-3">DATE</th>
                    <th className="px-4 py-3">VOUCHER #</th>
                    <th className="px-4 py-3">TYPE</th>
                    <th className="px-4 py-3">NARRATION</th>
                    <th className="px-4 py-3 text-right">DEBIT (₹)</th>
                    <th className="px-4 py-3 text-right">CREDIT (₹)</th>
                    <th className="px-4 py-3 text-right">RUNNING BALANCE (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {filteredLedgerEntries.length > 0 ? (
                    filteredLedgerEntries.map((row: any, i: number) => (
                      <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3 text-[#64748b]">{row.date}</td>
                        <td className="px-4 py-3 font-medium text-[#4f46e5]">{row.voucher}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded text-[10px] bg-slate-100 font-semibold text-slate-700">
                            {row.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#334155]">{row.narration}</td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">
                          {row.debit > 0 ? formatINR(row.debit) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-rose-600">
                          {row.credit > 0 ? formatINR(row.credit) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-[#1e293b]">
                          {formatINR(row.balance)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-[#94a3b8] italic">
                        No ledger entries found for the selected account filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 4: Trial Balance */}
        {activeTab === 'trial_balance' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#1e293b]">Trial Balance</h3>
                <p className="text-xs text-[#64748b]">Summary of all Ledger balances as of {toDate}</p>
              </div>
              <button
                onClick={handleExportCSV}
                className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-download-line text-xs" />
                <span>Export CSV</span>
              </button>
            </div>

            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                    <th className="px-4 py-3">CODE</th>
                    <th className="px-4 py-3">ACCOUNT NAME</th>
                    <th className="px-4 py-3">GROUP</th>
                    <th className="px-4 py-3 text-right">DEBIT (₹)</th>
                    <th className="px-4 py-3 text-right">CREDIT (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {trialBalanceRows.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3 font-mono text-[#64748b]">{row.code}</td>
                      <td className="px-4 py-3 font-medium text-[#1e293b]">{row.name}</td>
                      <td className="px-4 py-3 text-[#64748b]">{row.group}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#1e293b]">
                        {row.debit > 0 ? formatINR(row.debit) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-[#1e293b]">
                        {row.credit > 0 ? formatINR(row.credit) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#f8fafc] border-t-2 border-[#e2e8f0] font-bold text-xs text-[#1e293b]">
                    <td className="px-4 py-3" colSpan={3}>Total Balance</td>
                    <td className="px-4 py-3 text-right text-indigo-700">
                      {formatINR(trialBalanceRows.reduce((s, r) => s + r.debit, 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-indigo-700">
                      {formatINR(trialBalanceRows.reduce((s, r) => s + r.credit, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* TAB 5: Forex */}
        {activeTab === 'forex' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#1e293b]">Foreign Exchange (Forex) Exposure</h3>
                <p className="text-xs text-[#64748b]">Multi-currency holdings, valuation and unrealized gain/loss</p>
              </div>
              <button
                onClick={handleExportCSV}
                className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-download-line text-xs" />
                <span>Export CSV</span>
              </button>
            </div>

            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                    <th className="px-4 py-3">CURRENCY</th>
                    <th className="px-4 py-3 text-right">FOREIGN BALANCE</th>
                    <th className="px-4 py-3 text-right">BOOK RATE (₹)</th>
                    <th className="px-4 py-3 text-right">CURRENT RATE (₹)</th>
                    <th className="px-4 py-3 text-right">INR VALUATION</th>
                    <th className="px-4 py-3 text-right">UNREALIZED GAIN / LOSS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {forexRows.map((fx, i) => (
                    <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3 font-bold text-[#1e293b]">{fx.currency}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#1e293b]">
                        {fx.balance.toLocaleString()} {fx.code}
                      </td>
                      <td className="px-4 py-3 text-right text-[#64748b]">₹{fx.baseRate.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#64748b]">₹{fx.currentRate.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#1e293b]">
                        {formatINR(fx.valInr)}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold ${fx.unrealizedGain >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {fx.unrealizedGain >= 0 ? `+${formatINR(fx.unrealizedGain)}` : formatINR(fx.unrealizedGain)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
